import {
  ActionType,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingDisplayNameLink,
  SortDirections,
  stateT,
  Status,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  DetailsPanelId,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
  VMCLUSTER_CREATE_ROUTE,
} from "../../constants/pluginConstants";
import { ListingTestIds, MonochromeIconIds, ttlOneMinCaching } from "../../constants/uiConstants";
import {
  CloudExadataInfrastructure,
  CloudVmClusterSummary,
  CloudVmClusterSummaryCollection,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { getStatusInfo, optimizedRetryOption, responseItemstoArray } from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getAzureLocationName, getOciRegion } from "../../utils";

/**
 * TODO
 * This interface is implement to include the last status and details.
 * Api has not expose these two fields for now. When it will, we might remove this interface.
 */
interface CloudVmClusterSummaryExt extends CloudVmClusterSummary {
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
}

export interface ExaInfraVmClustersListProps {
  infra: CloudExadataInfrastructure;
  resGroup: string;
  subscriptionId: string;
  location: string;
}

interface VmClusterSummary {
  id: string
  name: string;
  location: string;
  resourceType: VmClusterTypes;
  cpuCoreCount?: number;
}

enum VmClusterTypes {
  Exadata = "exadata",
}

export enum ActionIds {
  CreateVmCluster = "create-vmcluster",
  Refresh = "refresh",
}

export enum ActionBarTestIds {
  CreateVmCluster = "create-vmcluster",
  Refresh = "refresh",
}

export enum ColumnIds {
  Name = "name",
  ResourceGroupName = "id",
  Location = "location",
  ResourceStatus = "status",
  OCPUCount = "cpuCoreCount",
  ResourceType = "resourceType",
}

export enum ColumnTestIds {
  Name = "name",
  ResourceGroupName = "id",
  Location = "location",
  ResourceStatus = "status",
  OCPUCount = "cpuCoreCount",
  ResourceType = "resourceType",
}

const exaVmClusterDetailsPageRegistrationIds = PageRegistrationConfig[PageId.VMCLUSTER_DETAILS].map(
  config => config.key,
);

export const ExaInfraVmClustersList = (
  { infra, resGroup, subscriptionId, location }: ExaInfraVmClustersListProps,
): JSX.Element => {
  const { trackActionClick } = useAnalytics();

  const { navigateTo } = useNavigation(ConsoleContext);
  const { locale } = React.useContext<Settings>(SettingsContext);

  const {
    response: listVmClustersResponse,
    loading: listVmClustersLoading,
    refresh: listVmClustersRefresh,
  } = useQueryCall({
    wait: !(subscriptionId && resGroup && location),
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.listCloudVmClusters,
    options: {
      args: {
        subscriptionId: subscriptionId || "",
        resourceGroupName: resGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadVMClusters(),
      },
    },
  });

  const vmClusterItems = (listVmClustersResponse
    && responseItemstoArray<CloudVmClusterSummaryCollection, CloudVmClusterSummary>(listVmClustersResponse))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ?.filter((vmClusterItem: CloudVmClusterSummary) => vmClusterItem.cloudExadataInfrastructureId === infra.id)
    .map((vmClusterItem: CloudVmClusterSummary) => ({
      id: vmClusterItem.id,
      name: vmClusterItem.name,
      location: vmClusterItem.location,
      status: vmClusterItem.status,
      cpuCoreCount: vmClusterItem.cpuCoreCount,
      resourceType: VmClusterTypes.Exadata,
    } as VmClusterSummary));

  const getLocation = (value: VmClusterSummary): JSX.Element => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <div>{getAzureLocationName(value?.location as any as string)}</div>
  );

  const getResourceType = (value: VmClusterSummary): JSX.Element => (
    <div>
      {value.resourceType === VmClusterTypes.Exadata
        ? Messages.labels.exaCS() : Messages.labels.autonomousDatabaseDedicated()}
    </div>
  );

  const getStatus = (value: CloudVmClusterSummaryExt): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
          value?.lifecycleDetails,
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.Name,
      testId: ColumnTestIds.Name,
      name: Messages.common.name(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getName(value),
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      itemProp: ColumnIds.Location,
      testId: ColumnTestIds.Location,
      name: Messages.labels.location(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.ResourceStatus,
      testId: ColumnTestIds.ResourceStatus,
      name: Messages.labels.status(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
    {
      itemProp: ColumnIds.OCPUCount,
      testId: ColumnTestIds.OCPUCount,
      name: Messages.labels.ocpuCount(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
    },
    {
      itemProp: ColumnIds.ResourceType,
      testId: ColumnTestIds.ResourceType,
      name: Messages.labels.resourceType(),
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getResourceType(value),
    },
  ];

  const refreshVmClusters = (): void => {
    listVmClustersRefresh();
  };

  const vmActions: ActionType[] = [];

  vmActions.push(
    {
      key: ActionIds.CreateVmCluster,
      testId: ActionBarTestIds.CreateVmCluster,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        trackActionClick(ActionIds.CreateVmCluster, PageId.EXAINFRA_DETAILS, DetailsPanelId.VMCLUSTERS);
        navigateTo(
          VMCLUSTER_CREATE_ROUTE,
          PageId.VMCLUSTER_CREATE,
          {
            infraId: infra.id,
            location: infra.location,
            analytics: {
              pageId: PageId.EXAINFRA_DETAILS,
              panelId: DetailsPanelId.VMCLUSTERS,
              actionName: ActionIds.CreateVmCluster,
            } as NavigationAnalyticsData,
          },
        );
      },
    },
  );

  vmActions.push(
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.EXAINFRA_DETAILS, DetailsPanelId.VMCLUSTERS);
        refreshVmClusters();
      },
    },
  );

  const getName = (value: VmClusterSummary): JSX.Element => (
    <div>
      <ListingDisplayNameLink
        displayName={value.name}
        navigation={{
          // eslint-disable-next-line max-len
          to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.VMCLUSTER_DETAILS][0].panelPath}?location=${value.location}`,
          pageKey: exaVmClusterDetailsPageRegistrationIds[0],
        }}
      />
    </div>
  );

  const items = React.useMemo(() => {
    const newVmClusters: VmClusterSummary[] = [];
    return newVmClusters.concat(vmClusterItems || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(vmClusterItems)]);

  return (
    <Listing
      testId={ListingTestIds.ExaInfraVmClusters}
      items={items}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      itemKey={ColumnIds.Name}
      actionBarItems={vmActions}
      isLoading={listVmClustersLoading}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.Name,
      }}
    />
  );
};
